import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  RiDashboardLine,
  RiPlaneLine,
  RiFileTextLine,
  RiTeamLine,
  RiCompassLine,
  RiMoreLine,
  RiUserLine,
  RiSettings4Line,
  RiLogoutBoxRLine,
  RiCloseLine
} from '@remixicon/react';
import { ROUTES } from '@/constants';
import { useAuthStore } from '@store/authStore';
import { supabase } from '@/lib/supabase';
import './BottomNavBar.css';

/**
 * BottomNavBar Component
 * Navigation bar fixed at the bottom of the screen
 *
 * @param {Object} props
 * @param {string} props.activeTab - Currently active tab (optional, will auto-detect from route)
 */
const BottomNavBar = ({ activeTab: propActiveTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { logout, setAuthType, userProfile } = useAuthStore();

  // Handle logout
  const handleLogout = async () => {
    logout();
    setAuthType('home');
    await supabase.auth.signOut();
    setIsMenuOpen(false);
    navigate('/');
  };

  // Define navigation items - matching desktop sidebar
  const navItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: RiDashboardLine,
      route: ROUTES.DASHBOARD.path,
    },
    {
      id: 'track',
      label: 'Track',
      icon: RiPlaneLine,
      route: ROUTES.TRACK.path,
    },
    {
      id: 'quotes',
      label: 'Quotes',
      icon: RiFileTextLine,
      route: ROUTES.QUOTES.path,
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: RiTeamLine,
      route: ROUTES.CLIENTS.path,
    },
    {
      id: 'financials',
      label: 'Financials',
      icon: RiCompassLine,
      route: ROUTES.FINANCIALS.path,
    },
  ];

  // Determine active tab from prop or current route
  const getActiveTab = () => {
    if (propActiveTab) return propActiveTab;

    const currentPath = location.pathname;
    const activeItem = navItems.find((item) => currentPath.startsWith(item.route));
    return activeItem ? activeItem.id : '';
  };

  const activeTabId = getActiveTab();

  const handleNavClick = (route) => {
    navigate(route);
  };

  return (
    <>
      <nav className="bottom-nav-bar" role="navigation" aria-label="Main navigation">
        <div className="nav-container">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTabId === item.id;
            return (
              <button
                key={item.id}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => handleNavClick(item.route)}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="nav-icon-svg" strokeWidth={isActive ? 2 : 1.5} />
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
          {/* More Menu Button */}
          <button
            className={`nav-item ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(true)}
            aria-label="More options"
          >
            <RiMoreLine className="nav-icon-svg" strokeWidth={1.5} />
            <span className="nav-label">More</span>
          </button>
        </div>
      </nav>

      {/* More Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 pb-24 animate-slide-in-from-bottom">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Menu</h3>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close menu"
              >
                <RiCloseLine className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* User Info */}
            {userProfile && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center font-medium">
                  {userProfile.first_name?.charAt(0) || userProfile.email?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {userProfile.first_name} {userProfile.last_name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{userProfile.email}</p>
                </div>
              </div>
            )}

            {/* Menu Items */}
            <div className="space-y-1">
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/profile');
                }}
                className="w-full flex items-center gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
              >
                <RiUserLine className="w-5 h-5" />
                <span className="font-medium">Profile</span>
              </button>

              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/settings');
                }}
                className="w-full flex items-center gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
              >
                <RiSettings4Line className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </button>

              <div className="my-2 border-t border-gray-100" />

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 p-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <RiLogoutBoxRLine className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavBar;
